/**
 * "Model another home" (downsizing rebuild, 21 Oct 2026; the agreed mock-up's j2-step1).
 *
 * Opened from the plan page or Home. Everything is carried over from the chosen plan — the sale, the state,
 * the years and the answers — and only the home changes: its name, so the plans can be told apart, and its
 * price. The duty and the money freed up update as the price is typed. The plans are then re-tested for
 * this home, saved as its own plan; the chosen plan stays chosen until the client chooses again.
 */
import React from 'react';
import { ArrowRight } from 'lucide-react';
import { CurrencyInput } from '../../components/ui/index.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import { STAMP_DUTY_STATES } from '../../../../shared/src/calculation/stampDuty.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { dzSaleFigures, dzDutyFor } from './dzSaleFigures.ts';

interface DzAnotherHomeProps {
    factFind: DraftFactFind;
    setFactFind?: React.Dispatch<React.SetStateAction<DraftFactFind>>;
    salePrice: number;
    sellingCosts: number;
    state: string;
    /** The chosen plan's title, for the line that says what is carried over. */
    chosenTitle: string | null;
    onRun: () => void;
    onCancel: () => void;
}

const money = (n: number) => formatCurrency(Math.round(n));

const DzAnotherHome = ({ factFind, setFactFind, salePrice, sellingCosts, state, chosenTitle, onRun, onCancel }: DzAnotherHomeProps) => {
    const v = householdVoice(factFind);
    const goals = (factFind?.goals || {}) as { dzHomeName?: string; dzNewHomePrice?: number };
    const setGoal = (patch: Record<string, unknown>) => setFactFind?.((prev) => ({ ...prev, goals: { ...(prev?.goals || {}), ...patch } }));
    const price = Number(goals.dzNewHomePrice) > 0 ? Number(goals.dzNewHomePrice) : 0;
    const duty = dzDutyFor(state, price);
    const f = dzSaleFigures({ salePrice, sellingCosts, newHomePrice: price, stampDuty: duty, sellAndRent: false });
    const stateName = STAMP_DUTY_STATES.find((s) => s.code === state)?.name;

    return (
        <div className="space-y-5" data-testid="dz-another-home">
            <p className="text-[15px] leading-relaxed text-slate-700">
                {price > 0 ? <>{v.you ? "You've" : `${v.Names} ${v.have}`} found a home for <strong>{money(price)}</strong>. </> : <>Enter the home {v.you ? "you've" : `${v.they}'ve`} found. </>}
                Everything else is carried over{chosenTitle ? <> from the chosen plan, &ldquo;{chosenTitle}&rdquo;</> : ''}; this saves as its own plan, and the chosen one stays chosen.
            </p>
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
                <p className="text-[12px] font-semibold text-brand-navy">The new home</p>
                <div className="mt-3 grid grid-cols-1 gap-x-7 gap-y-4 md:grid-cols-2">
                    <div>
                        <label htmlFor="dz-home-name" className="text-[13px] font-semibold text-slate-600">Name, so the plans can be told apart</label>
                        <input id="dz-home-name" type="text" value={String(goals.dzHomeName || '')} onChange={(e) => setGoal({ dzHomeName: e.target.value.slice(0, 80) })} placeholder="e.g. Elm Street townhouse" className="mt-1 block w-full max-w-sm rounded-lg border border-slate-300 px-3 py-2 text-[15px]" data-testid="dz-home-name" />
                    </div>
                    <div>
                        <p className="text-[13px] font-semibold text-slate-600">Price</p>
                        <CurrencyInput value={price || ''} onChange={(x) => setGoal({ dzNewHomePrice: Math.max(0, x ?? 0), dzHousing: 'buy' })} className="mt-1 w-48" ariaLabel="The home's price" testId="dz-another-price" />
                    </div>
                </div>
                {price > 0 && (
                    <table className="mt-4 w-full max-w-xl text-[14px] tabular-nums" data-testid="dz-another-sums"><tbody>
                        <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-700">Sale, less costs</td><td className="py-1.5 text-right">{money(f.netProceeds)}</td></tr>
                        <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-700">New home</td><td className="py-1.5 text-right">−{money(price)}</td></tr>
                        <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-700">Stamp duty{stateName ? `, ${stateName}` : ''}</td><td className="py-1.5 text-right">{stateName ? `−${money(f.stampDuty)}` : 'none — no state chosen on step 1'}</td></tr>
                        <tr><td className="py-1.5 font-semibold text-slate-900">{f.buyUpShortfall >= 1 ? 'From savings' : 'Money freed up'}</td><td className="py-1.5 text-right font-bold text-slate-900" data-testid="dz-another-freed">{money(f.buyUpShortfall >= 1 ? f.buyUpShortfall : f.freedCapital)}</td></tr>
                    </tbody></table>
                )}
            </section>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <button type="button" onClick={onCancel} className="text-[14px] font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4">Back to the plan</button>
                <button type="button" onClick={onRun} disabled={!(price > 0)} data-testid="dz-another-run"
                    className="inline-flex h-11 items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 px-5 text-[14px] font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50">
                    Test the ways to use the money <ArrowRight size={16} />
                </button>
            </div>
        </div>
    );
};

export default DzAnotherHome;
